import { useState } from "react";
import { Input, DatePicker, Select, Radio, Tooltip, Switch } from "antd";
import dayjs from "dayjs";

import stateTaxRates from "../components/Calculator10b51/json/stateTaxRates.json";
import {
  formatNumberPlace,
  RadioSelector,
} from "../utils/Constants/systemSetting";

const strip = {
  dollar: (v) => v.replace(/[^0-9.-]/g, ""), // remove $, commas, spaces
  dollarFloat: (v) => v.replace(/[^0-9.-]/g, ""), // remove $, commas, spaces
  number: (v) => v.replace(/[^0-9.-]/g, ""), // remove $, commas, spaces
  percent: (v) => v.replace(/[^0-9.-]/g, ""), // remove %, spaces
  float: (v) => v.replace(/[^0-9.-]/g, ""), // remove %, spaces
  string: (v) => v.replace(/[^a-zA-Z0-9 _-]/g, ""), // alphanumeric + space/dash/underscore
  date: (v) => v, // dayjs object, no stripping needed
  state: (v) => v, // from state selector, no stripping needed
  radio: (v) => v, // radio selector, no stripping needed
  switch: (v) => v, // switch, no stripping needed
  dropdown: (v) => v, // dropdown selector, no stripping needed
};

const format = {
  dollar: (v) => {
    const n = parseInt(v, 10);
    return isNaN(n) ? v : `$${n.toLocaleString()}`;
  },
  dollarFloat: (v) => {
    const n = parseFloat(v, 10);
    return isNaN(n)
      ? v
      : `${n.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })}`;
  },
  number: (v) => {
    const n = parseInt(v, 10);
    return isNaN(n) ? v : `${n.toLocaleString()}`;
  },
  percent: (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? v : `${n.toFixed(0)}%`;
  },
  float: (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? v : `${n.toFixed(1)}`;
  },
  string: (v) => {
    return v ?? "";
  },
  date: (v) => {
    // v is stored as a dayjs object (or null)
    return v ? dayjs(v).format("MMM D, YYYY") : "—";
  },
  state: (v) => v ?? "—",
  radio: (v) => v,
  switch: (v) => v,
  dropdown: (v) => v,
};

export const useEditableCell = () => {
  const [focusedCell, setFocusedCell] = useState(null);

  const renderEditableCell = (
    value,
    record,
    columnKey,
    type,
    handleChange,
    setCurrentEditedCell,
    customStyle = {},
    options = [],
  ) => {
    const cellKey = `${record.key}-${columnKey}`;
    const isFocused = focusedCell === cellKey;

    const onFocus = () => {
      if (isFocused) {
        setCurrentEditedCell(cellKey);
      }

      setFocusedCell(cellKey);
    };

    // Auto-populate values for the first column based on defaults
    const onBlur = () => {
      if (!value && ["grant1", "Short-Term", "Long-Term"].includes(columnKey)) {
        handleChange(columnKey, record, record?.defaultValue);
      }
      setFocusedCell(null);
    };

    if (record?.hasHoverText) {
      if (!value?.value && value?.value !== 0) {
        const formattedValue = format[type](value);
        return (
          <div style={{ width: "100%", cursor: "help" }}>
            <p
              style={{
                borderColor: "#E5E7EB",
                // width: "50%",
              }}
            >
              {formattedValue}
            </p>
          </div>
        );
      }

      const formattedValue = format[type](value.value);
      return (
        <Tooltip
          title={value.hoverText}
          overlayStyle={
            value?.hoverTextOverlayStyle
              ? { ...value?.hoverTextOverlayStyle }
              : {}
          }
        >
          <div style={{ width: "100%", cursor: "help" }}>
            <p
              style={{
                borderColor: "#E5E7EB",
                // width: "50%",
              }}
            >
              {formattedValue}
            </p>
          </div>
        </Tooltip>
      );
    }

    if (record?.isCalculatedValue) {
      const formattedValue = format[type](value);
      return (
        <div style={{ width: "100%" }}>
          <p
            style={{
              borderColor: "#E5E7EB",
              // width: "50%",
            }}
          >
            {formattedValue}
          </p>
        </div>
      );
    }

    if (type === "switch") {
      return (
        <Switch
          checked={value}
          onClick={() => handleChange(columnKey, record, !value)}
        />
      );
    }
    if (type === "state") {
      return {
        children: (
          <Select
            value={value || null}
            onChange={(val) => {
              handleChange(columnKey, record, val);
            }}
            options={stateTaxRates.map((stateTaxInfo) => ({
              label: stateTaxInfo?.State,
              value: stateTaxInfo?.State,
            }))}
            style={{ width: "50%", ...customStyle }}
            placeholder={
              <span style={{ color: "var(--dark-red)" }}>Select a State</span>
            }
          />
        ),
        // Currently defining colspan assuming the state selector is ONLY used in tax table
        // If needed, colSpan can made its own variable in this hook later.
        props: { colSpan: columnKey === "Short-Term" ? 2 : 0 },
      };
    }
    if (type === "dropdown") {
      return {
        children: (
          <Select
            value={value || null}
            onChange={(val) => {
              handleChange(columnKey, record, val);
            }}
            options={options.map((dayOfMonth) => ({
              label: formatNumberPlace(dayOfMonth),
              value: dayOfMonth,
            }))}
            style={{ width: "50%", ...customStyle }}
          />
        ),
      };
    }

    // ── Date: render DatePicker instead of Input ──
    if (type === "date") {
      return (
        <DatePicker
          value={value ? dayjs(value) : null}
          format="M/D/YY"
          allowClear={false}
          onChange={(dayjsVal) => {
            handleChange(columnKey, record, dayjsVal);
          }}
          style={{
            borderColor: "#E5E7EB",
            width: "50%",
            ...customStyle,
          }}
        />
      );
    }

    if (type === "radio") {
      return (
        <Radio.Group
          size="medium"
          onChange={(e) => {
            handleChange(columnKey, record, e.target.value);
          }}
          value={value}
        >
          {options.map((option) => (
            <Radio.Button
              value={option}
              key={option}
              className="custom-radio-button"
              style={{ margin: 5 }}
            >
              {option}
            </Radio.Button>
          ))}
        </Radio.Group>
      );
    }

    // ── Text / Number types: Input ──
    return (
      <Input
        value={isFocused ? value : format[type](value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => {
          const stripped = strip[type](e.target.value);
          handleChange(columnKey, record, stripped);
        }}
        style={{
          borderColor: "#E5E7EB",
          width: "50%",
          ...customStyle,
        }}
      />
    );
  };

  return renderEditableCell;
};
